// ────────────────────────────────────────────────────────────────────
// InboxContextPane — the inbox's right column (2026-07-23 cross-tenant-inbox
// spec, PR 2): "บริบทลูกค้า" — open งาน (with ball-in-court) + the last few
// completed deliveries, for whichever tenant the middle pane is currently
// showing.
//
// Reads the SAME ["conversation"] query TeamChatBody already fetches for
// that middle pane (identical queryKey + queryFn) — react-query dedups the
// network call while both are mounted, same "one query, two consumers"
// pattern TeamChatDockMounter already uses alongside TeamChatBody itself
// (see TeamChatDock.tsx). No new backend endpoint.
//
// Finding (worth flagging to whoever wrote the spec's hedge): the payload
// does NOT force an approved-only fallback — ConversationDelivery already
// carries certified_by/certified_cert/certified_at alongside
// client_approved_by/at (same fields DeliveryCard's own certified/approved
// treatment reads, see server/schemas.py's ConversationDelivery). So a
// completed+certified งาน gets the ShieldCheck mark here, matching
// DeliveryCard's own "client_stage==='done' && certified_by" rule exactly —
// nothing invented.
// ────────────────────────────────────────────────────────────────────
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, CheckCircle2, FileText } from "lucide-react";
import { apiClient } from "@/lib/api";
import { BallChip } from "./TeamChatDock";
import type { Conversation, ConversationDelivery } from "@/lib/types";

const CLIENT_STAGE_LABEL: Record<string, string> = {
  received: "ได้รับแล้ว", processing: "กำลังทำ", done: "เสร็จ", cancelled: "ยกเลิก",
};

// How many most-recently-created completed deliveries to surface — a
// "recent activity" glance, not a full history (SubmissionsScreen's Work
// queue is that). created_at is the only timestamp ConversationDelivery
// carries (no per-delivery updated_at), so "most recent" reads as
// "most recently opened" here, same proxy the middle pane's own
// lastActivityBySub fallback already accepts.
const RECENT_COMPLETED_LIMIT = 3;

// Mirrors TeamChatBody's own scrollToEvent idiom (data-ev-id + smooth
// scrollIntoView): silently no-ops when the target card isn't in the
// currently-rendered DOM (a delivery scrolled out of the loaded window, or —
// same as a bad reply quote — just not there). DeliveryCard stamps
// data-delivery-id on its own root (see TeamChatDock.tsx).
function scrollToDelivery(id: number): void {
  document.querySelector(`[data-delivery-id="${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
}

function isOpenDelivery(d: ConversationDelivery): boolean {
  return d.status !== "completed" && d.status !== "cancelled";
}

function OpenRow({ d }: { d: ConversationDelivery }): JSX.Element {
  return (
    <button type="button" onClick={() => scrollToDelivery(d.id)}
            data-testid={`inbox-open-row-${d.id}`}
            className="w-full text-left px-3 py-2 border-b hover:bg-[rgba(31,78,121,0.04)]"
            style={{ borderColor: "var(--hairline)" }}>
      <div className="flex items-center justify-between gap-2 mb-0.5">
        <span className="font-mono truncate" style={{ fontSize: 11, color: "var(--ink-1)", fontWeight: 600 }}>
          {d.ref} · {d.title}
        </span>
        <div className="shrink-0"><BallChip status={d.status} /></div>
      </div>
      <div className="flex items-center gap-1.5 font-mono" style={{ fontSize: 9.5, color: "var(--ink-4)" }}>
        <span>{CLIENT_STAGE_LABEL[d.client_stage] ?? d.client_stage}</span>
        {d.file_count > 0 && (
          <span className="inline-flex items-center gap-0.5"><FileText size={10} /> {d.file_count}</span>
        )}
      </div>
    </button>
  );
}

// Completed row's certified/approved mark — certified takes precedence (a
// certified delivery is always also "done", but showing BOTH marks on one
// tiny row would be visual noise for no extra information the certified
// stamp doesn't already imply more strongly).
function CompletedMark({ d }: { d: ConversationDelivery }): JSX.Element | null {
  if (d.certified_by) {
    return (
      <span className="inline-flex items-center gap-1 font-mono" style={{ fontSize: 9.5, color: "var(--ok)" }}>
        <ShieldCheck size={11} /> รับรองแล้ว
      </span>
    );
  }
  if (d.client_approved_by) {
    return (
      <span className="inline-flex items-center gap-1 font-mono" style={{ fontSize: 9.5, color: "var(--ok)" }}>
        <CheckCircle2 size={11} /> อนุมัติแล้ว
      </span>
    );
  }
  return null;
}

function CompletedRow({ d }: { d: ConversationDelivery }): JSX.Element {
  return (
    <button type="button" onClick={() => scrollToDelivery(d.id)}
            data-testid={`inbox-completed-row-${d.id}`}
            className="w-full text-left px-3 py-2 border-b hover:bg-[rgba(31,78,121,0.04)]"
            style={{ borderColor: "var(--hairline)" }}>
      <div className="font-mono truncate mb-0.5" style={{ fontSize: 11, color: "var(--ink-2)" }}>
        {d.ref} · {d.title}
      </div>
      <CompletedMark d={d} />
    </button>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="font-mono uppercase px-3 py-1.5" style={{
      fontSize: 9, letterSpacing: ".14em", color: "var(--ink-4)", fontWeight: 700,
      background: "var(--paper-2)", borderBottom: "1px solid var(--hairline)" }}>
      {children}
    </div>
  );
}

function SectionEmpty({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="font-mono px-3 py-3" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>{children}</div>
  );
}

export function InboxContextPane(): JSX.Element {
  const convQ = useQuery({
    queryKey: ["conversation"], queryFn: () => apiClient.submissionConversation(), staleTime: 0,
  });
  const conv: Conversation | undefined = convQ.data;

  const open = useMemo(() => (conv?.deliveries ?? []).filter(isOpenDelivery), [conv]);
  const completed = useMemo(() => (conv?.deliveries ?? [])
    .filter((d) => d.status === "completed")
    .sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0))
    .slice(0, RECENT_COMPLETED_LIMIT), [conv]);

  return (
    <div data-testid="inbox-context-pane" className="flex flex-col h-full min-h-0">
      <div className="px-3 py-2.5 border-b shrink-0" style={{ borderColor: "var(--hairline)" }}>
        <div className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: ".18em", color: "var(--accent-deep)", fontWeight: 700 }}>
          บริบทลูกค้า
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        {convQ.isPending && <SectionEmpty>กำลังโหลด…</SectionEmpty>}
        {convQ.isError && <SectionEmpty>โหลดไม่สำเร็จ</SectionEmpty>}
        {!convQ.isPending && !convQ.isError && (
          <>
            <SectionHeader>งานเปิด</SectionHeader>
            {open.length === 0
              ? <SectionEmpty>ยังไม่มีงานเปิด</SectionEmpty>
              : open.map((d) => <OpenRow key={d.id} d={d} />)}
            <SectionHeader>เสร็จล่าสุด</SectionHeader>
            {completed.length === 0
              ? <SectionEmpty>ยังไม่มีงานที่เสร็จ</SectionEmpty>
              : completed.map((d) => <CompletedRow key={d.id} d={d} />)}
          </>
        )}
      </div>
    </div>
  );
}
